import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { EDIT_TRANSACTION } from '../graphql/mutations/transactions';
import { TransactionType } from '../utils/enums';

const summarizeAssignmentError = (message) => {
  const text = String(message || '').trim();
  if (!text) return 'Could not assign location.';
  if (text.toLowerCase().includes('insufficient holdings')) {
    return 'Insufficient holdings after assigning this location.';
  }
  return text.replace(/\s+/g, ' ').slice(0, 140);
};

const getTransactionType = (transaction) => transaction.transactionType?.toUpperCase?.() || transaction.transactionType;

const getTransactionLabel = (transaction) => {
  const txType = getTransactionType(transaction);
  const typeName = TransactionType.getDisplayName?.(txType) || txType || 'Transaction';
  const date = transaction.timestamp ? new Date(transaction.timestamp).toLocaleDateString() : '';
  return [typeName, date].filter(Boolean).join(' · ');
};

const assignmentPriority = (transaction) => {
  const txType = getTransactionType(transaction);
  if (txType === TransactionType.BUY || txType === TransactionType.TRANSFER_IN) return 0;
  if (txType === TransactionType.SELL || txType === TransactionType.TRANSFER_OUT) return 1;
  return 2;
};

const sortForAssignment = (left, right) => {
  const priorityDiff = assignmentPriority(left) - assignmentPriority(right);
  if (priorityDiff !== 0) return priorityDiff;

  const leftTime = left.timestamp ? new Date(left.timestamp).getTime() : 0;
  const rightTime = right.timestamp ? new Date(right.timestamp).getTime() : 0;
  if (leftTime !== rightTime) return leftTime - rightTime;

  return (left.id || '').localeCompare(right.id || '');
};

const AssignLocationModal = ({ isOpen, onClose, transactions = [], portfolioId, tokenLocations = [], onAssigned }) => {
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [error, setError] = useState(null);
  const [resultMessage, setResultMessage] = useState(null);
  const [editTransaction, { loading }] = useMutation(EDIT_TRANSACTION);

  const assignableTransactions = transactions
    .filter((tx) => ![TransactionType.INTERNAL_TRANSFER, TransactionType.SPLIT].includes(getTransactionType(tx)))
    .sort(sortForAssignment);
  const skippedInternalTransfers = transactions.length - assignableTransactions.length;
  const count = assignableTransactions.length;

  useEffect(() => {
    if (isOpen) {
      setSelectedLocationId('');
      setError(null);
      setResultMessage(null);
    }
  }, [isOpen]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedLocationId || count === 0) return;

    setError(null);
    setResultMessage(null);

    const failures = [];
    let assignedCount = 0;

    for (const transaction of assignableTransactions) {
      try {
        const { data } = await editTransaction({
          variables: {
            transactionId: transaction.id,
            portfolioId,
            locationId: selectedLocationId,
          },
        });

        const result = data?.editTransaction;
        if (!result?.success) {
          failures.push(`${getTransactionLabel(transaction)}: ${summarizeAssignmentError(result?.error)}`);
          continue;
        }
        assignedCount += 1;
      } catch (err) {
        failures.push(`${getTransactionLabel(transaction)}: ${summarizeAssignmentError(err.message)}`);
      }
    }

    const failedCount = failures.length;
    const skippedText = skippedInternalTransfers > 0
      ? ` Skipped ${skippedInternalTransfers} internal transfer/split transaction${skippedInternalTransfers === 1 ? '' : 's'}.`
      : '';

    if (failedCount > 0) {
      setResultMessage(
        `Assigned ${assignedCount} of ${count} transaction${count === 1 ? '' : 's'}. ${failedCount} failed.${skippedText}`
      );
      setError(failures.slice(0, 5).join('\n'));
      onAssigned?.({ assignedCount, failedCount, skippedInternalTransfers, locationId: selectedLocationId, hasFailures: true });
      return;
    }

    onAssigned?.({ assignedCount, failedCount, skippedInternalTransfers, locationId: selectedLocationId, hasFailures: false });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 px-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Assign location
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100"
          >
            <span className="sr-only">Close</span>
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Assign location to {count} selected transaction{count === 1 ? '' : 's'}.
          </p>

          {skippedInternalTransfers > 0 && (
            <div className="rounded-md bg-amber-50 dark:bg-amber-900/20 p-3">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                {skippedInternalTransfers} internal transfer/split transaction{skippedInternalTransfers === 1 ? '' : 's'} will be skipped. Internal transfers use source/destination locations; splits apply across all locations.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Location
            </label>
            <select
              value={selectedLocationId}
              onChange={(event) => setSelectedLocationId(event.target.value)}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              disabled={loading || tokenLocations.length === 0}
            >
              <option value="">Select location</option>
              {tokenLocations.map((location) => (
                <option key={location.id} value={location.id}>{location.name}</option>
              ))}
            </select>
          </div>

          {tokenLocations.length === 0 && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-3">
              <p className="text-sm text-red-700 dark:text-red-300">No locations available for this portfolio.</p>
            </div>
          )}

          <div className="rounded-md bg-gray-50 dark:bg-gray-900/30 p-3">
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Location balances are recalculated after saving. The batch assigns buys first, then sells, so sells can use newly assigned incoming balance. Impossible sells/transfers may be excluded.
            </p>
          </div>

          {resultMessage && (
            <div className="rounded-md bg-amber-50 dark:bg-amber-900/20 p-3">
              <p className="text-sm text-amber-700 dark:text-amber-300 whitespace-pre-line">{resultMessage}</p>
            </div>
          )}

          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-3">
              <p className="text-sm text-red-700 dark:text-red-300 whitespace-pre-line">{error}</p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 rounded-md border border-transparent text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
              disabled={loading || !selectedLocationId || count === 0}
            >
              {loading ? 'Assigning...' : 'Assign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignLocationModal;
